import { TERMINAL_STEP_TYPES } from "@sagot/constants";
import type { Step } from "../../types";

/**
 * Whether every outcome of the step has its own path, so the run never falls through to the next step. Only steps whose
 * outcomes cover everything that can happen count: an assistant answer (answered / couldn't answer / asked for a person),
 * a booking card (booked / not booked), a lead form (sent / not sent), a webhook wait (received / timed out), a data connector request (success / failure),
 * a customer reply (replied / no reply in time), a snooze (woke / interrupted) and an e-mail check (verified / failed). A wait or a rating that was skipped still
 * carries on with the next step.
 */
export function isFullyRouted(step: Step): boolean {
  switch (step.type) {
    case "let_assistant_answer":
      return Boolean(step.answered_goto && step.not_answered_goto && step.asked_for_person_goto);
    case "show_booking":
      return Boolean(step.booked_goto && step.not_booked_goto);
    case "show_lead_form":
      return Boolean(step.submitted_goto && step.not_submitted_goto);
    case "wait_for_webhook":
      return Boolean(step.received_goto && step.timeout_goto);
    case "http_request":
      return Boolean(step.success_goto && step.failure_goto);
    case "collect_reply":
      return Boolean(step.replied_goto && step.no_reply_after && step.no_reply_goto);
    case "snooze":
      return Boolean(step.woke_goto && step.interrupted_goto);
    case "verify_email":
      return Boolean(step.verified_goto && step.failed_goto);
    default:
      return false;
  }
}

/** A step after which nothing else in the path can run (reply buttons, branch, go to path, end, hand to a person, or fully routed). */
export function isTerminalStep(step: Step): boolean {
  return TERMINAL_STEP_TYPES.has(step.type) || isFullyRouted(step);
}

const chosen = (goto: string | undefined): string | undefined => (goto === undefined || goto === "" ? undefined : goto);

/** The path the step's first exit leads to; `undefined` when the step has no exit or the first one is not chosen. */
export function firstExitOf(step: Step): string | undefined {
  switch (step.type) {
    case "reply_buttons":
      return chosen(step.buttons[0]?.goto);
    case "branch":
      return chosen(step.branches[0]?.goto);
    case "go_to_path":
      return chosen(step.path);
    case "let_assistant_answer":
      return chosen(step.answered_goto);
    case "show_booking":
      return chosen(step.booked_goto);
    case "show_lead_form":
      return chosen(step.submitted_goto);
    case "wait":
      return chosen(step.interrupted_goto);
    case "wait_for_webhook":
      return chosen(step.received_goto);
    case "ask_rating":
      return chosen(step.rated_goto);
    case "http_request":
      return chosen(step.success_goto);
    case "collect_reply":
      return chosen(step.replied_goto);
    case "snooze":
      return chosen(step.woke_goto);
    case "verify_email":
      return chosen(step.verified_goto);
    default:
      return undefined;
  }
}

/** The step with its first exit leading to `target`; a step without exits comes back unchanged. */
export function withFirstExit(step: Step, target: string): Step {
  switch (step.type) {
    case "reply_buttons":
      return { ...step, buttons: step.buttons.map((button, index) => (index === 0 ? { ...button, goto: target } : button)) };
    case "branch":
      return { ...step, branches: step.branches.map((branch, index) => (index === 0 ? { ...branch, goto: target } : branch)) };
    case "go_to_path":
      return { ...step, path: target };
    case "let_assistant_answer":
      return { ...step, answered_goto: target };
    case "show_booking":
      return { ...step, booked_goto: target };
    case "show_lead_form":
      return { ...step, submitted_goto: target };
    case "wait":
      return { ...step, interrupted_goto: target };
    case "wait_for_webhook":
      return { ...step, received_goto: target };
    case "ask_rating":
      return { ...step, rated_goto: target };
    case "http_request":
      return { ...step, success_goto: target };
    case "collect_reply":
      return { ...step, replied_goto: target };
    case "snooze":
      return { ...step, woke_goto: target };
    case "verify_email":
      return { ...step, verified_goto: target };
    default:
      return step;
  }
}
